import { inserisciMessaggio } from '@/lib/qsocials/dm';
import type { DmConversation, DmDeleteResult, DmMessage, DmMessagesPage } from '@/types/dm';

import { socialApi } from './socialApi';

/**
 * DM fra amici — le sei rotte di QSocials `/social/dm` e la cancellazione (contratto
 * `fronti/qsocials/tempo-reale-e-dm.md`, §1 e §1-ter). Iniettate in `socialApi`: stesso token, stesso
 * cancello di `VITE_FEATURE_SOCIAL_ENABLED`, stesso `X-Request-Id` per le riletture d'evento.
 */
const conv = (id: string) => `/social/dm/conversations/${encodeURIComponent(id)}`;
export const DM_PAGINA = 30;

export const dmApi = socialApi.injectEndpoints({
  endpoints: (build) => ({
    listDmConversations: build.query<DmConversation[], void>({
      query: () => ({ url: '/social/dm/conversations' }),
      transformResponse: (r: { conversations: DmConversation[] }) => r.conversations,
      providesTags: ['DmConversations'],
    }),
    getDmUnread: build.query<number, void>({
      query: () => ({ url: '/social/dm/unread' }),
      transformResponse: (r: { unread: number }) => r.unread,
      providesTags: ['DmUnread'],
    }),
    /** Idempotente e riservata agli amici: 403 `not_friends` altrimenti. */
    openDmConversation: build.mutation<DmConversation, string>({
      query: (friendUserId) => ({ url: '/social/dm/conversations', method: 'POST', body: { friendUserId } }),
      transformResponse: (r: { conversation: DmConversation }) => r.conversation,
      invalidatesTags: ['DmConversations'],
    }),
    /** La storia a pagine, dalla piu' recente; `fetchNextPage` porta la pagina PIU' VECCHIA. */
    getDmMessages: build.infiniteQuery<DmMessagesPage, string, number | null>({
      infiniteQueryOptions: {
        initialPageParam: null,
        getNextPageParam: (ultima) => ultima.nextCursor ?? undefined,
      },
      query: ({ queryArg, pageParam }) => ({
        url: `${conv(queryArg)}/messages`,
        params: pageParam === null ? { limit: DM_PAGINA } : { cursor: pageParam, limit: DM_PAGINA },
      }),
      transformResponse: (r: DmMessagesPage) => ({ messages: r.messages, nextCursor: r.nextCursor }),
      providesTags: (_r, _e, id) => [{ type: 'DmMessages', id }],
    }),
    /**
     * L'invio: il messaggio che torna entra nella storia SENZA una GET (lo stesso messaggio che poi
     * arriva dal canale si scarta per `seq`).
     */
    sendDmMessage: build.mutation<DmMessage, { conversationId: string; text: string }>({
      query: ({ conversationId, text }) => ({ url: `${conv(conversationId)}/messages`, method: 'POST', body: { text } }),
      transformResponse: (r: { message: DmMessage }) => r.message,
      async onQueryStarted({ conversationId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            dmApi.util.updateQueryData('getDmMessages', conversationId, (d) => {
              inserisciMessaggio(d.pages, data);
            }),
          );
        } catch {
          /* il rifiuto lo mostra la conversazione */
        }
      },
      invalidatesTags: ['DmConversations'],
    }),
    markDmRead: build.mutation<void, string>({
      query: (conversationId) => ({ url: `${conv(conversationId)}/read`, method: 'POST' }),
      invalidatesTags: ['DmUnread', 'DmConversations'],
    }),
    /**
     * D4 / P1: cancella TUTTI i messaggi scritti dall'utente, in tutte le conversazioni, anche dalla vista
     * dell'altro. `confirm=true` e' la conferma che il server pretende (senza: 400 `confirm_required`):
     * la manda solo il foglio di conferma (`components/dm/CancellaMieiMessaggi.tsx`).
     */
    deleteMyDmMessages: build.mutation<DmDeleteResult, void>({
      query: () => ({ url: '/social/dm/messages', method: 'DELETE', params: { confirm: 'true' } }),
      transformResponse: (r: DmDeleteResult) => ({ deleted: r.deleted, conversations: r.conversations }),
      invalidatesTags: ['DmConversations', 'DmUnread', 'DmMessages'],
    }),
  }),
});

export const {
  useListDmConversationsQuery,
  useGetDmUnreadQuery,
  useOpenDmConversationMutation,
  useGetDmMessagesInfiniteQuery,
  useSendDmMessageMutation,
  useMarkDmReadMutation,
  useDeleteMyDmMessagesMutation,
} = dmApi;
